/**
 * Turns an MSAL failure into something an officer can act on.
 *
 * Sign-in failures reach the user as a sentence on the sign-in page, so the text has to
 * say what to do next — retry, use a different account, or go to their own IT — without
 * leaking the raw AADSTS string into the UI. The code is still appended, because it is
 * what support asks for first and the user otherwise has nothing to quote.
 *
 * Only the cases a law-enforcement user realistically hits are named. Anything else gets
 * the generic sentence plus its code rather than a wrong guess.
 */
import { AuthError } from '@azure/msal-browser';

interface ErrorFacts {
  /** MSAL's own error code, e.g. `user_cancelled`, or `''`. */
  code: string;
  /** The AADSTS code Entra returned, e.g. `AADSTS53003`, or `''`. */
  aadsts: string;
}

function factsOf(error: unknown): ErrorFacts {
  const code = error instanceof AuthError ? error.errorCode : '';
  const text =
    error instanceof AuthError
      ? `${error.errorCode} ${error.errorMessage}`
      : error instanceof Error
        ? error.message
        : String(error ?? '');

  return { code, aadsts: /AADSTS\d+/i.exec(text)?.[0].toUpperCase() ?? '' };
}

/** The `(code)` suffix support asks for, or `''` when neither code is known. */
function reference({ code, aadsts }: ErrorFacts): string {
  const parts = [aadsts, code].filter(Boolean);
  return parts.length > 0 ? ` (${parts.join(' ')})` : '';
}

export function describeAuthError(error: unknown): string {
  const facts = factsOf(error);
  const { code, aadsts } = facts;
  const ref = reference(facts);

  // Cancelling is a choice, not a fault: no code, no apology.
  if (code === 'user_cancelled' || aadsts === 'AADSTS50199') {
    return 'Sign-in was cancelled. Select Sign in with Microsoft to try again.';
  }

  // AADSTS65004: the user declined the consent prompt. AADSTS650056 / 900971: the app
  // registration itself is wrong, which no retry fixes.
  if (code === 'consent_required' || aadsts === 'AADSTS65004') {
    return `Sign-in needs your permission to continue, and it was not granted. Try again and accept the prompt${ref}.`;
  }

  // AADSTS53000-53004 are the Conditional Access family: compliant device, MFA,
  // approved client. The user cannot resolve these; their own IT has to.
  if (code === 'interaction_required' || /^AADSTS5300[0-4]$/.test(aadsts)) {
    return `Your organization's security policy blocked this sign-in. Contact your IT administrator${ref}.`;
  }

  // AADSTS50020 / 700016: the account exists, but not in the tenant this portal accepts.
  // Distinct from the tenant backstop, which only fires after a token is issued.
  if (aadsts === 'AADSTS50020' || aadsts === 'AADSTS700016') {
    return `That account cannot be used with this portal. Sign in with the account issued to you for it${ref}.`;
  }

  return `Sign-in could not be completed. Try again, and contact your IT administrator if it keeps failing${ref}.`;
}
